class ProductAddons {
  constructor() {
    console.log('[ProductAddons] init');

    this.addonsBlock = document.querySelector('[data-product-addons]');

    this.pendingAddonVariantId = null;
    this.pendingParentVariantId = null;
    this.cartBeforeParentAdd = null;

    this.isProcessing = false;
    this.isRefreshingDrawer = false;

    this.parentPollTimer = null;
    this.cartWatcherTimer = null;
    this.normalizeCartTimer = null;
    this.hideQuantityTimer = null;

    this.observedRoots = new WeakSet();
    this.cachedAddonTitles = new Set();

    if (this.addonsBlock) {
      this.bindAddonSelect();
      this.bindParentCapture();
      this.initSelectedAddon();
    } else {
      console.log(
        '[ProductAddons] addons block not found; cart control remains active'
      );
    }

    this.bindCartWatchers();
    this.observeRechargeDrawer();

    window.setTimeout(() => {
      this.initialNormalize();
    }, 700);

    console.log('[ProductAddons] ready');
  }

  /* ==========================================================================
     INITIALIZATION
     ========================================================================== */

  async initialNormalize() {
    try {
      const changed = await this.normalizeAddonItems();

      await this.updateAddonTitleCache();

      this.hideCachedAddonQuantitySelectors();

      if (changed) {
        this.requestCartDrawerRefresh();
      }
    } catch (error) {
      console.error(
        '[ProductAddons] initial normalization error:',
        error
      );
    }
  }

  /* ==========================================================================
     ADDON SELECTION
     ========================================================================== */

  bindAddonSelect() {
    document.addEventListener('change', (event) => {
      const input = event.target.closest?.('[data-addon-input]');

      if (!input) return;

      const addonVariantId = Number(input.value);

      if (!addonVariantId || Number.isNaN(addonVariantId)) {
        console.error(
          '[ProductAddons] invalid addon variant ID:',
          input.value
        );

        this.pendingAddonVariantId = null;
        return;
      }

      this.pendingAddonVariantId = addonVariantId;

      console.log(
        '[ProductAddons] addon selected:',
        addonVariantId
      );

      /*
       * Сохраняем состояние корзины заранее.
       * Recharge может добавить основной товар очень быстро.
       */
      this.captureCartBeforeParent('addon-select').catch((error) => {
        console.error(
          '[ProductAddons] addon selection snapshot error:',
          error
        );
      });
    });
  }

  /* ==========================================================================
     CAPTURE PARENT BEFORE RECHARGE ATC
     ========================================================================== */
  initSelectedAddon() {
    const checked = this.addonsBlock?.querySelector(
      '[data-addon-input]:checked'
    );

    if (!checked) return;

    this.pendingAddonVariantId = Number(checked.value);

    console.log(
      '[ProductAddons] initial addon:',
      this.pendingAddonVariantId
    );
  }
  bindParentCapture() {
    const addToCartSelector = [
      'button[name="add"]',
      'button[type="submit"].product-form__submit',
      '[data-add-to-cart]',
      '[data-testid="AddToCart"]'
    ].join(',');

    /*
     * pointerdown срабатывает раньше большинства обработчиков Recharge.
     */
    document.addEventListener(
      'pointerdown',
      (event) => {
        const button = event.target.closest?.(addToCartSelector);

        if (!button || !this.pendingAddonVariantId) return;

        const form = this.getProductForm(button);

        if (!form) {
          console.warn(
            '[ProductAddons] product form not found on pointerdown'
          );

          return;
        }

        this.pendingParentVariantId =
          this.getVariantIdFromForm(form);

        console.log('[ProductAddons] ATC pointerdown:', {
          parentVariantId: this.pendingParentVariantId,
          addonVariantId: this.pendingAddonVariantId
        });

        this.captureCartBeforeParent('pointerdown').catch((error) => {
          console.error(
            '[ProductAddons] pointerdown snapshot error:',
            error
          );
        });
      },
      true
    );

    /*
     * Ничего не отменяем. Recharge продолжает штатно добавлять parent.
     */
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest?.(addToCartSelector);

        if (!button || !this.pendingAddonVariantId) return;

        const form = this.getProductForm(button);

        if (!form) {
          console.warn(
            '[ProductAddons] product form not found on click'
          );

          return;
        }

        this.pendingParentVariantId =
          this.getVariantIdFromForm(form);

        console.log('[ProductAddons] ATC click:', {
          parentVariantId: this.pendingParentVariantId,
          addonVariantId: this.pendingAddonVariantId
        });

        if (!this.cartBeforeParentAdd) {
          this.captureCartBeforeParent('click').catch((error) => {
            console.error(
              '[ProductAddons] click snapshot error:',
              error
            );
          });
        }

        this.startParentPolling();
      },
      true
    );

    /*
     * Запасной сценарий, если тема добавляет товар через submit.
     */
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;

        if (
          !(form instanceof HTMLFormElement) ||
          !form.matches('form[action*="/cart/add"]') ||
          !this.pendingAddonVariantId
        ) {
          return;
        }

        this.pendingParentVariantId =
          this.getVariantIdFromForm(form);

        console.log('[ProductAddons] product form submit:', {
          parentVariantId: this.pendingParentVariantId,
          addonVariantId: this.pendingAddonVariantId
        });

        if (!this.cartBeforeParentAdd) {
          this.captureCartBeforeParent('submit').catch((error) => {
            console.error(
              '[ProductAddons] submit snapshot error:',
              error
            );
          });
        }

        this.startParentPolling();
      },
      true
    );
  }

  getProductForm(button = null) {
    return (
      button?.closest?.('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  getVariantIdFromForm(form) {
    if (!form) return null;

    const formData = new FormData(form);
    const variantId = Number(formData.get('id'));

    if (!variantId || Number.isNaN(variantId)) {
      console.error(
        '[ProductAddons] parent variant ID not found in form'
      );

      return null;
    }

    return variantId;
  }

  async captureCartBeforeParent(source) {
    const cart = await this.getCart();

    this.cartBeforeParentAdd = cart;

    console.log('[ProductAddons] cart snapshot:', {
      source,
      items: this.summarizeCart(cart)
    });

    return cart;
  }

  /* ==========================================================================
     WAIT FOR RECHARGE TO ADD PARENT
     ========================================================================== */

  startParentPolling() {
    window.clearTimeout(this.parentPollTimer);

    const startedAt = Date.now();
    const timeout = 12000;

    const check = async () => {
      if (
        !this.pendingAddonVariantId ||
        !this.pendingParentVariantId
      ) {
        console.log(
          '[ProductAddons] parent polling stopped: no pending data'
        );

        return;
      }

      if (this.isProcessing) {
        this.parentPollTimer = window.setTimeout(check, 400);
        return;
      }

      try {
        const currentCart = await this.getCart();

        const parentLine = this.findAddedParentLine(
          this.cartBeforeParentAdd,
          currentCart,
          this.pendingParentVariantId
        );

        console.log('[ProductAddons] parent polling:', {
          found: Boolean(parentLine),
          parentVariantId: this.pendingParentVariantId
        });

        if (parentLine) {
          await this.attachAddonToParent(
            parentLine,
            currentCart
          );

          return;
        }
      } catch (error) {
        console.error(
          '[ProductAddons] parent polling error:',
          error
        );
      }

      if (Date.now() - startedAt >= timeout) {
        console.warn(
          '[ProductAddons] parent polling timeout'
        );

        this.clearPendingSelection();
        return;
      }

      this.parentPollTimer = window.setTimeout(check, 400);
    };

    this.parentPollTimer = window.setTimeout(check, 350);
  }

  findAddedParentLine(
    beforeCart,
    afterCart,
    parentVariantId
  ) {
    const beforeItems = beforeCart?.items || [];

    const beforeLines = beforeItems.filter((item) => {
      return (
        Number(item.variant_id) === Number(parentVariantId) &&
        !this.isAddonItem(item)
      );
    });

    const afterLines = afterCart.items.filter((item) => {
      return (
        Number(item.variant_id) === Number(parentVariantId) &&
        !this.isAddonItem(item)
      );
    });

    const beforeKeys = new Set(
      beforeLines.map((item) => item.key)
    );

    /*
     * Сначала ищем полностью новую строку.
     */
    const newLine = afterLines.find((item) => {
      return !beforeKeys.has(item.key);
    });

    if (newLine) {
      console.log(
        '[ProductAddons] new parent line found:',
        newLine
      );

      return newLine;
    }

    /*
     * Если этот variant уже был в корзине,
     * Shopify может увеличить quantity существующей строки.
     */
    const increasedLine = afterLines.find((afterItem) => {
      const beforeItem = beforeLines.find((item) => {
        return item.key === afterItem.key;
      });

      return (
        beforeItem &&
        Number(afterItem.quantity) > Number(beforeItem.quantity)
      );
    });

    if (increasedLine) {
      console.log(
        '[ProductAddons] increased parent line found:',
        increasedLine
      );

      return increasedLine;
    }

    return null;
  }

  /* ==========================================================================
     MANUAL PARENT-CHILD RELATIONSHIP
     ========================================================================== */

  async attachAddonToParent(parentLine, currentCart) {
    if (this.isProcessing) return;

    this.isProcessing = true;

    window.clearTimeout(this.parentPollTimer);

    const addonVariantId = this.pendingAddonVariantId;

    console.log('[ProductAddons] attach addon:', {
      addonVariantId,
      parentKey: parentLine.key,
      parentVariantId: parentLine.variant_id,
      parentTitle: parentLine.product_title
    });

    try {
      /*
       * По требованиям во всей корзине может быть только один addon.
       */
      await this.removeAllExistingAddons(currentCart);

      await this.addAddon(
        addonVariantId,
        parentLine
      );

      const updatedCart = await this.getCart();

      await this.normalizeAddonItems(updatedCart);
      await this.updateAddonTitleCache(updatedCart);

      this.clearPendingSelection();
      this.requestCartDrawerRefresh();

      this.scheduleHideQuantity(200);
      this.scheduleHideQuantity(700);
      this.scheduleHideQuantity(1500);
    } catch (error) {
      console.error(
        '[ProductAddons] attach addon error:',
        error
      );
    } finally {
      window.setTimeout(() => {
        this.isProcessing = false;

        console.log(
          '[ProductAddons] processing unlocked'
        );
      }, 900);
    }
  }

  async addAddon(variantId, parentLine) {
    const payload = {
      id: Number(variantId),
      quantity: 1,
      properties: {
        _addon_product: 'true',

        /*
         * Точная ссылка на конкретную строку parent.
         */
        _addon_parent_line_key: String(parentLine.key),

        /*
         * Сохраняем также variant ID только для информации и логов.
         * Для проверки существования parent он не используется.
         */
        _addon_parent_variant_id: String(
          parentLine.variant_id
        ),

        _addon_created_at: String(Date.now())
      }
    };

    console.log(
      '[ProductAddons] addAddon payload:',
      payload
    );

    const response = await fetch(
      `${window.Shopify?.routes?.root || '/'}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );

    const data = await response.json().catch(() => null);

    console.log('[ProductAddons] addAddon response:', {
      status: response.status,
      ok: response.ok,
      data
    });

    if (!response.ok) {
      throw new Error(
        data?.description ||
        data?.message ||
        'Addon was not added'
      );
    }

    return data;
  }

  async removeAllExistingAddons(cart = null) {
    const currentCart = cart || await this.getCart();

    const addonItems = currentCart.items.filter((item) => {
      return this.isAddonItem(item);
    });

    console.log(
      '[ProductAddons] existing addons to remove:',
      addonItems
    );

    for (const addon of addonItems) {
      await this.changeCartItem(addon.key, 0);
    }
  }

  /* ==========================================================================
     NORMALIZATION
     ========================================================================== */

  async normalizeAddonItems(cart = null) {
    console.log(
      '[ProductAddons] normalizeAddonItems start'
    );

    let currentCart = cart || await this.getCart();
    let changed = false;

    let addonItems = currentCart.items.filter((item) => {
      return this.isAddonItem(item);
    });

    console.log(
      '[ProductAddons] addons before normalization:',
      addonItems.map((item) => ({
        title: item.product_title || item.title,
        key: item.key,
        quantity: item.quantity,
        parentKey: this.getAddonParentKey(item),
        properties: item.properties
      }))
    );

    /*
     * Удаляем addon, если конкретная parent line больше не существует.
     */
    for (const addon of addonItems) {
      const parentExists = this.hasExistingParent(
        addon,
        currentCart
      );

      if (parentExists) continue;

      console.warn(
        '[ProductAddons] REMOVE ORPHAN ADDON:',
        {
          addon: addon.product_title || addon.title,
          addonKey: addon.key,
          parentKey: this.getAddonParentKey(addon)
        }
      );

      await this.changeCartItem(
        addon.key,
        0
      );

      changed = true;
    }

    if (changed) {
      currentCart = await this.getCart();

      addonItems = currentCart.items.filter((item) => {
        return this.isAddonItem(item);
      });
    }

    /*
     * Во всей корзине может быть только один addon.
     * Оставляем самый новый.
     */
    if (addonItems.length > 1) {
      const sortedAddons = [...addonItems].sort((a, b) => {
        return (
          this.getAddonCreatedAt(b) -
          this.getAddonCreatedAt(a)
        );
      });

      const addonToKeep = sortedAddons[0];
      const addonsToRemove = sortedAddons.slice(1);

      console.log(
        '[ProductAddons] addon to keep:',
        addonToKeep.key
      );

      for (const addon of addonsToRemove) {
        console.warn(
          '[ProductAddons] remove duplicate addon:',
          addon.key
        );

        await this.changeCartItem(
          addon.key,
          0
        );

        changed = true;
      }

      currentCart = await this.getCart();

      addonItems = currentCart.items.filter((item) => {
        return this.isAddonItem(item);
      });
    }

    /*
     * Quantity addon всегда должно быть 1.
     */
    const remainingAddon = addonItems[0];

    if (
      remainingAddon &&
      Number(remainingAddon.quantity) !== 1
    ) {
      console.warn(
        '[ProductAddons] restore addon quantity to 1:',
        remainingAddon.key
      );

      await this.changeCartItem(
        remainingAddon.key,
        1
      );

      changed = true;
    }

    this.cachedAddonTitles = new Set(
      addonItems.map((item) => {
        return this.normalizeText(
          item.product_title || item.title
        );
      })
    );

    console.log(
      '[ProductAddons] normalizeAddonItems end:',
      {
        changed,
        addonTitles: [...this.cachedAddonTitles]
      }
    );

    return changed;
  }

  hasExistingParent(addon, cart) {
    const parentKey = this.getAddonParentKey(addon);

    if (!parentKey) {
      console.warn(
        '[ProductAddons] addon has no parent key:',
        {
          addon: addon.product_title || addon.title,
          properties: addon.properties
        }
      );

      return false;
    }

    /*
     * Проверяем строго по line key.
     * Fallback по variant ID не используем.
     */
    const parentExists = cart.items.some((item) => {
      return (
        !this.isAddonItem(item) &&
        String(item.key) === String(parentKey)
      );
    });

    console.log('[ProductAddons] parent check:', {
      addon: addon.product_title || addon.title,
      parentKey,
      parentExists
    });

    return parentExists;
  }

  isAddonItem(item) {
    return (
      item?.properties &&
      String(item.properties._addon_product) === 'true'
    );
  }

  getAddonParentKey(item) {
    const value =
      item?.properties?._addon_parent_line_key;

    return value ? String(value) : null;
  }

  getAddonParentVariantId(item) {
    const value = Number(
      item?.properties?._addon_parent_variant_id
    );

    return value && !Number.isNaN(value)
      ? value
      : null;
  }

  getAddonCreatedAt(item) {
    const value = Number(
      item?.properties?._addon_created_at
    );

    return value && !Number.isNaN(value)
      ? value
      : 0;
  }

  /* ==========================================================================
     CART WATCHERS
     ========================================================================== */

  bindCartWatchers() {
    const run = (source) => {
      console.log(
        '[ProductAddons] cart watcher:',
        source
      );

      window.clearTimeout(this.cartWatcherTimer);

      this.cartWatcherTimer = window.setTimeout(() => {
        this.afterCartUpdated(source).catch((error) => {
          console.error(
            '[ProductAddons] cart watcher error:',
            error
          );
        });
      }, 700);

      this.scheduleHideQuantity(350);
      this.scheduleHideQuantity(900);
    };

    document.addEventListener(
      'cart:updated',
      (event) => {
        if (event.detail?.source === 'product-addons') {
          return;
        }

        run('cart:updated');
      }
    );

    document.addEventListener(
      'cart:refresh',
      (event) => {
        if (event.detail?.source === 'product-addons') {
          return;
        }

        run('cart:refresh');
      }
    );

    document.addEventListener(
      'recharge:cart:updated',
      (event) => {
        if (event.detail?.source === 'product-addons') {
          return;
        }

        run('recharge:cart:updated');
      }
    );

    document.addEventListener(
      'recharge:cart:refresh',
      (event) => {
        if (event.detail?.source === 'product-addons') {
          console.log(
            '[ProductAddons] ignore own Recharge refresh event'
          );

          return;
        }

        run('recharge:cart:refresh');
      }
    );

    /*
     * Проверяем корзину после действий внутри drawer.
     */
    document.addEventListener(
      'click',
      (event) => {
        if (this.isInsideCartDrawer(event.target, event)) {
          console.log(
            '[ProductAddons] cart drawer click detected'
          );

          run('cart-drawer-click');

          this.scheduleCartNormalization(500);
          this.scheduleCartNormalization(1200);
        }

        this.scheduleHideQuantity(350);
      },
      true
    );

    document.addEventListener(
      'change',
      (event) => {
        if (!this.isInsideCartDrawer(event.target, event)) {
          return;
        }

        run('cart-drawer-change');
        this.scheduleCartNormalization(600);
      },
      true
    );

    window.addEventListener('focus', () => {
      this.scheduleCartNormalization(400);
    });
  }

  async afterCartUpdated(source) {
    if (this.isProcessing) {
      console.log(
        '[ProductAddons] processing; watcher skipped:',
        source
      );

      return;
    }

    const currentCart = await this.getCart();

    console.log('[ProductAddons] cart updated:', {
      source,
      items: this.summarizeCart(currentCart)
    });

    /*
     * Если Recharge только что добавил parent,
     * прикрепляем выбранный addon.
     */
    if (
      this.pendingAddonVariantId &&
      this.pendingParentVariantId
    ) {
      const parentLine = this.findAddedParentLine(
        this.cartBeforeParentAdd,
        currentCart,
        this.pendingParentVariantId
      );

      if (parentLine) {
        await this.attachAddonToParent(
          parentLine,
          currentCart
        );

        return;
      }
    }

    const changed = await this.normalizeAddonItems(
      currentCart
    );

    await this.updateAddonTitleCache();

    this.hideCachedAddonQuantitySelectors();

    if (changed) {
      this.requestCartDrawerRefresh();
    }
  }

  scheduleCartNormalization(delay = 500) {
    window.clearTimeout(this.normalizeCartTimer);

    this.normalizeCartTimer = window.setTimeout(async () => {
      if (this.isProcessing) {
        console.log(
          '[ProductAddons] normalization delayed: processing active'
        );

        this.scheduleCartNormalization(700);
        return;
      }

      try {
        console.log(
          '[ProductAddons] scheduled cart normalization'
        );

        const changed = await this.normalizeAddonItems();

        await this.updateAddonTitleCache();

        this.hideCachedAddonQuantitySelectors();

        if (changed) {
          console.log(
            '[ProductAddons] cart changed after normalization'
          );

          this.requestCartDrawerRefresh();
        }
      } catch (error) {
        console.error(
          '[ProductAddons] scheduled normalization error:',
          error
        );
      }
    }, delay);
  }

  async changeCartItem(key, quantity) {
    console.log(
      '[ProductAddons] change cart item:',
      {
        key,
        quantity
      }
    );

    const response = await fetch(
      `${window.Shopify?.routes?.root || '/'}cart/change.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      }
    );

    const data = await response.json().catch(() => null);

    console.log(
      '[ProductAddons] change response:',
      {
        status: response.status,
        ok: response.ok,
        data
      }
    );

    if (!response.ok) {
      throw new Error(
        data?.description ||
        data?.message ||
        'Cart item could not be changed'
      );
    }

    return data;
  }

  /* ==========================================================================
     HIDE ADDON QUANTITY IN RECHARGE DRAWER
     ========================================================================== */

  async updateAddonTitleCache(cart = null) {
    const currentCart = cart || await this.getCart();

    const addonItems = currentCart.items.filter((item) => {
      return this.isAddonItem(item);
    });

    this.cachedAddonTitles = new Set(
      addonItems.map((item) => {
        return this.normalizeText(
          item.product_title || item.title
        );
      })
    );

    console.log(
      '[ProductAddons] cached addon titles:',
      [...this.cachedAddonTitles]
    );
  }

  hideCachedAddonQuantitySelectors() {
    if (!this.cachedAddonTitles.size) return;

    const roots = this.getAllSearchRoots();

    roots.forEach((root) => {
      const listItems = root.querySelectorAll?.(
        '#cart-drawer [role="listitem"], [role="listitem"]'
      );

      if (!listItems) return;

      listItems.forEach((listItem) => {
        const titleElement =
          listItem.querySelector('p.font-semibold');

        if (!titleElement) return;

        const drawerTitle = this.normalizeText(
          titleElement.textContent
        );

        if (!this.cachedAddonTitles.has(drawerTitle)) {
          return;
        }

        const quantityInput = listItem.querySelector(
          'input[name="quantity"], input[aria-label="Quantity"]'
        );

        if (!quantityInput) return;

        const quantityWrapper = quantityInput.parentElement;

        if (!quantityWrapper) return;

        /*
         * Уже скрытый элемент повторно не меняем.
         */
        if (
          quantityWrapper.dataset.addonQuantityHidden === 'true'
        ) {
          return;
        }

        quantityWrapper.dataset.addonQuantityHidden = 'true';

        quantityWrapper.classList.add(
          'product-addons-hidden-quantity'
        );

        quantityWrapper.style.setProperty(
          'display',
          'none',
          'important'
        );

        quantityWrapper.setAttribute(
          'aria-hidden',
          'true'
        );

        if (
          !listItem.querySelector(
            '.product-addons-static-quantity'
          )
        ) {
          const staticQuantity =
            document.createElement('span');

          staticQuantity.className =
            'product-addons-static-quantity';

          staticQuantity.textContent = 'Qty: 1';

          staticQuantity.style.fontSize = '14px';
          staticQuantity.style.lineHeight = '1.4';
          staticQuantity.style.whiteSpace = 'nowrap';

          const actionsRow =
            quantityWrapper.parentElement;

          actionsRow?.prepend(staticQuantity);
        }

        console.log(
          '[ProductAddons] addon quantity hidden once:',
          drawerTitle
        );
      });
    });
  }

  scheduleHideQuantity(delay = 150) {
    window.clearTimeout(this.hideQuantityTimer);

    this.hideQuantityTimer = window.setTimeout(() => {
      /*
       * Здесь нет запроса /cart.js.
       * Используется уже подготовленный cache.
       */
      this.hideCachedAddonQuantitySelectors();
    }, delay);
  }

  normalizeText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /* ==========================================================================
     RECHARGE DRAWER OBSERVER
     ========================================================================== */

  observeRechargeDrawer() {
    console.log(
      '[ProductAddons] observe Recharge drawer'
    );

    const registerObservers = () => {
      const roots = this.getAllSearchRoots();

      roots.forEach((root) => {
        if (this.observedRoots.has(root)) {
          return;
        }

        this.observedRoots.add(root);

        const observer = new MutationObserver((mutations) => {
          const hasRealCartChange = mutations.some((mutation) => {
            if (mutation.type !== 'childList') {
              return false;
            }

            const changedNodes = [
              ...mutation.addedNodes,
              ...mutation.removedNodes
            ];

            return changedNodes.some((node) => {
              if (!(node instanceof HTMLElement)) {
                return false;
              }

              /*
               * Игнорируем элементы, добавленные нашим кодом.
               */
              if (
                node.classList.contains(
                  'product-addons-static-quantity'
                ) ||
                node.classList.contains(
                  'product-addons-hidden-quantity'
                )
              ) {
                return false;
              }

              return (
                node.matches?.(
                  '[role="listitem"], #cart-drawer'
                ) ||
                Boolean(
                  node.querySelector?.(
                    '[role="listitem"], #cart-drawer'
                  )
                )
              );
            });
          });

          if (!hasRealCartChange) return;

          console.log(
            '[ProductAddons] real Recharge cart DOM change'
          );

          this.scheduleCartNormalization(500);
          this.scheduleHideQuantity(700);
        });

        observer.observe(root, {
          childList: true,
          subtree: true

          /*
           * attributes не слушаем, иначе изменение style/class
           * quantity selector создаст цикл.
           */
        });

        console.log(
          '[ProductAddons] observing:',
          root === document
            ? 'document'
            : 'shadowRoot'
        );
      });
    };

    registerObservers();

    this.shadowRootScanInterval = window.setInterval(() => {
      registerObservers();
    }, 1500);
  }

  getAllSearchRoots() {
    const roots = [document];
    const visited = new WeakSet();
    const queue = [document];

    while (queue.length) {
      const root = queue.shift();

      if (!root || visited.has(root)) {
        continue;
      }

      visited.add(root);

      const elements =
        root.querySelectorAll?.('*') || [];

      elements.forEach((element) => {
        if (
          element.shadowRoot &&
          !visited.has(element.shadowRoot)
        ) {
          roots.push(element.shadowRoot);
          queue.push(element.shadowRoot);
        }
      });
    }

    return roots;
  }

  isInsideCartDrawer(element, event = null) {
    if (!element) return false;

    if (element.closest?.('#cart-drawer')) {
      return true;
    }

    /*
     * Для событий, пришедших из Shadow DOM.
     */
    const eventPath = event?.composedPath?.() || [];

    const drawerInPath = eventPath.some((node) => {
      return (
        node instanceof HTMLElement &&
        (
          node.id === 'cart-drawer' ||
          Boolean(node.closest?.('#cart-drawer'))
        )
      );
    });

    if (drawerInPath) return true;

    return this.getAllSearchRoots().some((root) => {
      const drawer = root.querySelector?.('#cart-drawer');

      return drawer?.contains?.(element) || false;
    });
  }

  /* ==========================================================================
     CART HELPERS
     ========================================================================== */

  async getCart() {
    const response = await fetch(
      `${window.Shopify?.routes?.root || '/'}cart.js`,
      {
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to load cart: ${response.status}`
      );
    }

    return response.json();
  }

  summarizeCart(cart) {
    return (cart?.items || []).map((item) => {
      return {
        title: item.product_title || item.title,
        key: item.key,
        variantId: item.variant_id,
        quantity: item.quantity,
        isAddon: this.isAddonItem(item),
        parentKey: this.getAddonParentKey(item),
        parentVariantId:
          this.getAddonParentVariantId(item)
      };
    });
  }

  clearPendingSelection() {
    console.log(
      '[ProductAddons] clear pending selection'
    );

    window.clearTimeout(this.parentPollTimer);

    this.pendingAddonVariantId = null;
    this.pendingParentVariantId = null;
    this.cartBeforeParentAdd = null;

    this.addonsBlock
      ?.querySelectorAll('[data-addon-input]:checked')
      .forEach((input) => {
        input.checked = false;
      });
  }

  /* ==========================================================================
     RECHARGE REFRESH
     ========================================================================== */

  requestCartDrawerRefresh() {
    if (this.isRefreshingDrawer) {
      console.log(
        '[ProductAddons] drawer refresh already running'
      );

      return;
    }

    this.isRefreshingDrawer = true;

    console.log(
      '[ProductAddons] request Recharge drawer refresh'
    );

    /*
     * Не отправляем cart:updated и cart:refresh,
     * потому что сами их слушаем.
     */
    document.dispatchEvent(
      new CustomEvent('recharge:cart:refresh', {
        detail: {
          source: 'product-addons'
        }
      })
    );

    window.setTimeout(() => {
      this.isRefreshingDrawer = false;

      this.scheduleHideQuantity(300);
      this.scheduleCartNormalization(900);
    }, 700);
  }
}

/* ============================================================================
   INITIALIZATION
   ============================================================================ */

const initProductAddons = () => {
  if (window.__productAddonsInstance) {
    console.log(
      '[ProductAddons] already initialized'
    );

    return;
  }

  window.__productAddonsInstance =
    new ProductAddons();
};

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initProductAddons,
    {
      once: true
    }
  );
} else {
  initProductAddons();
}